import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  deleteDoc,
  increment,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../utils/firebase";

export interface VotingProject {
  id: string;
  name: string;
  group: string;
  poster?: string;
  youtubeUrl?: string;
  voteCount: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface VoteRecord {
  id: string;
  projectId: string;
  deviceFingerprint: string;
  votedAt: Timestamp;
}

/**
 * Create a new voting project
 */
export const createVotingProject = async (
  project: Omit<VotingProject, "id" | "voteCount" | "createdAt" | "updatedAt" | "youtubeUrl"> & { youtubeUrl?: string }
): Promise<string> => {
  const projectsRef = collection(db, "votingProjects");
  const newProjectRef = doc(projectsRef);
  const projectId = newProjectRef.id;

  await setDoc(newProjectRef, {
    ...project,
    id: projectId,
    voteCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return projectId;
};

/**
 * Get all voting projects
 */
export const getVotingProjects = async (): Promise<VotingProject[]> => {
  const projectsRef = collection(db, "votingProjects");
  const q = query(projectsRef, orderBy("createdAt", "desc"));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as VotingProject[];
};

/**
 * Get a single voting project by ID
 */
export const getVotingProject = async (
  projectId: string
): Promise<VotingProject | null> => {
  const projectRef = doc(db, "votingProjects", projectId);
  const projectSnap = await getDoc(projectRef);

  if (projectSnap.exists()) {
    return {
      id: projectSnap.id,
      ...projectSnap.data(),
    } as VotingProject;
  }

  return null;
};

/**
 * Subscribe to real-time updates for all voting projects
 */
export const subscribeToVotingProjects = (
  callback: (projects: VotingProject[]) => void
): (() => void) => {
  const projectsRef = collection(db, "votingProjects");
  const q = query(projectsRef, orderBy("voteCount", "desc"));

  return onSnapshot(q, (querySnapshot) => {
    const projects = querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    })) as VotingProject[];

    callback(projects);
  });
};

/**
 * Subscribe to real-time updates for a single project
 */
export const subscribeToVotingProject = (
  projectId: string,
  callback: (project: VotingProject | null) => void
): (() => void) => {
  const projectRef = doc(db, "votingProjects", projectId);

  return onSnapshot(projectRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({
        id: docSnap.id,
        ...docSnap.data(),
      } as VotingProject);
    } else {
      callback(null);
    }
  });
};

/**
 * Check if a device has already voted for a project (server-side check)
 */
export const hasDeviceVotedForProject = async (
  projectId: string,
  deviceFingerprint: string
): Promise<boolean> => {
  const votesRef = collection(db, "voteRecords");
  const q = query(
    votesRef,
    where("projectId", "==", projectId),
    where("deviceFingerprint", "==", deviceFingerprint)
  );

  const querySnapshot = await getDocs(q);
  return !querySnapshot.empty;
};

/**
 * Submit a vote for a project
 */
export const submitVote = async (
  projectId: string,
  deviceFingerprint: string
): Promise<{ success: boolean; message: string }> => {
  try {
    // Check if device has already voted
    const hasVoted = await hasDeviceVotedForProject(
      projectId,
      deviceFingerprint
    );

    if (hasVoted) {
      return {
        success: false,
        message: "You have already voted for this project.",
      };
    }

    // Check if project exists
    const project = await getVotingProject(projectId);
    if (!project) {
      return {
        success: false,
        message: "Project not found.",
      };
    }

    // Create vote record
    const votesRef = collection(db, "voteRecords");
    const voteRecordRef = doc(votesRef);
    await setDoc(voteRecordRef, {
      id: voteRecordRef.id,
      projectId,
      deviceFingerprint,
      votedAt: serverTimestamp(),
    });

    // Update project vote count
    const projectRef = doc(db, "votingProjects", projectId);
    await updateDoc(projectRef, {
      voteCount: increment(1),
      updatedAt: serverTimestamp(),
    });

    return {
      success: true,
      message: "Vote submitted successfully!",
    };
  } catch (error) {
    console.error("Error submitting vote:", error);
    return {
      success: false,
      message: "An error occurred while submitting your vote.",
    };
  }
};

/**
 * Remove a vote (if needed for undo functionality)
 */
export const removeVote = async (
  projectId: string,
  deviceFingerprint: string
): Promise<{ success: boolean; message: string }> => {
  try {
    // Find the vote record
    const votesRef = collection(db, "voteRecords");
    const q = query(
      votesRef,
      where("projectId", "==", projectId),
      where("deviceFingerprint", "==", deviceFingerprint)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return {
        success: false,
        message: "No vote found to remove.",
      };
    }

    // Delete vote records (in case of duplicates)
    const deletePromises = querySnapshot.docs.map((docSnap) => {
      const voteRef = doc(db, "voteRecords", docSnap.id);
      return deleteDoc(voteRef);
    });

    await Promise.all(deletePromises);

    // Update project vote count
    const projectRef = doc(db, "votingProjects", projectId);
    const projectSnap = await getDoc(projectRef);

    if (projectSnap.exists()) {
      const currentCount = projectSnap.data().voteCount || 0;
      await updateDoc(projectRef, {
        voteCount: Math.max(0, currentCount - querySnapshot.docs.length),
        updatedAt: serverTimestamp(),
      });
    }

    return {
      success: true,
      message: "Vote removed successfully!",
    };
  } catch (error) {
    console.error("Error removing vote:", error);
    return {
      success: false,
      message: "An error occurred while removing your vote.",
    };
  }
};

/**
 * Get vote count for a specific project
 */
export const getProjectVoteCount = async (projectId: string): Promise<number> => {
  const project = await getVotingProject(projectId);
  return project?.voteCount || 0;
};

